/*
 * TunePad
 *
 * Michael S. Horn
 * Northwestern University
 * michael-horn@northwestern.edu
 *
 * This project was funded by the National Science Foundation (grant DRL-1612619).
 * Any opinions, findings and conclusions or recommendations expressed in this
 * material are those of the author(s) and do not necessarily reflect the views
 * of the National Science Foundation (NSF).
 */
import styles from './message.module.css' assert { type: 'css' };
import html from './message.module.html';
import errorIcon from './error-icon.svg';
import infoIcon from './info-icon.svg';
import warnIcon from './warn-icon.svg';
import crossIcon from './cross-icon.svg';
import checkIcon from './check-icon.svg';
import googleLogo from './google-logo.svg';
import ddgLogo from './ddg-logo.svg';


/**
 * <python-message type="test" pass="true">{ message }</python-message>
 */
export class PythonMessage extends HTMLElement {

    static DefineElement() {
        if (customElements.get('python-message') === undefined) {
            customElements.define("python-message", PythonMessage);
        }
    }

    static observedAttributes = [
        'type',    // "error" | "message" | "warning" | "test" (default is "message")
        'pass',    // "true" | "false" (default is "false")
        'details'  // stack trace for error message
    ];

    private pass = false;
    private type = "message";
    private message = "";
    private details ? : string;

    /// all of the HTML elements for the instrument are contained within a shadow DOM
    private root : ShadowRoot;

    constructor() {
        super();
        this.root = this.attachShadow({ mode: 'open' });
        this.root.adoptedStyleSheets.push(styles);
        this.root.innerHTML = html;
        this.setHTML('.ggl-search', googleLogo);
        this.setHTML('.ddg-search', ddgLogo);
    }

    connectedCallback() {
        this.message = this.root.host.innerHTML.trim();
        this.setFields();

        this.root.querySelector('#expand-button')?.addEventListener('pointerup', (evt) => {
            this.addClass('#expand-button', 'hidden');
            this.removeClass('#collapse-button', 'hidden');
            this.removeClass('.details-box', 'hidden');
        });
        this.root.querySelector('#collapse-button')?.addEventListener('pointerup', (evt) => {
            this.removeClass('#expand-button', 'hidden');
            this.addClass('#collapse-button', 'hidden');
            this.addClass('.details-box', 'hidden');
        });
    }

    disconnectedCallback() { }

    attributeChangedCallback(name : string, oldValue : string, newValue : string) {
        if (newValue === oldValue) return;
        if (name === 'type' && [ 'message', 'error', 'warning', 'test' ].includes(newValue)) {
            this.type = newValue;
        } else if (name === 'pass') {
            this.pass = (newValue === 'true');
        } else if (name === 'details') {
            this.details = newValue;
        }
        this.setFields();
    }

    private setFields() {
        this.addClass('.python-message', this.type);
        this.setHTML('#message', this.message);
        this.setHTML('#details', this.details ? this.details : 'No stack trace available.');
        if (this.pass) this.addClass('.python-message', 'pass');
        this.setHTML('#status', this.pass ? 'PASSED' : 'FAILED');
        
        if (this.type === 'message') {
            this.setHTML('.icon', infoIcon);
        } else if (this.type === 'warning') {
            this.setHTML('.icon', warnIcon);
        } else if (this.type === 'error') {
            this.setHTML('.icon', errorIcon);
        } else if (this.type === 'test' && this.pass) {
            this.setHTML('.icon', checkIcon);
        } else if (this.type === 'test') {
            this.setHTML('.icon', crossIcon);
        }
        const q = encodeURI(`q=${this.message.replace(/<[^>]*>?/gm, '')}`);
        this.root.querySelector('.ddg-search')?.setAttribute('href', '//duckduckgo.com?' + q);
        this.root.querySelector('.ggl-search')?.setAttribute('href', '//google.com?' + q);
    }

    private setHTML(selector : string, html : string) {
        const e = this.root.querySelector(selector);
        if (e) e.innerHTML = html;
    }

    private addClass(selector : string, className : string) {
        const e = this.root.querySelector(selector);
        if (e) e.classList.add(className);
    }

    private removeClass(selector : string, className : string) {
        const e = this.root.querySelector(selector);
        if (e) e.classList.remove(className);
    }
}
